import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, TextField, Button, IconButton, Modal,
    Typography, Paper, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Checkbox, FormControlLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchPlansFromAPI,
    createPlanInAPI,
    updatePlanInAPI,
    deletePlanFromAPI
} from '../../../Redux/SubscriptionPlansDucks';

const formatCLP = (v) => '$' + (Number(v) || 0).toLocaleString('es-CL');

const SubscriptionPlansPage = () => {
    const dispatch = useDispatch();
    const plans = useSelector(store => store.subscriptionPlans.plans);

    const [loading, setLoading] = useState(true);
    const [filterText, setFilterText] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);

    // Form state
    const [name, setName] = useState('');
    const [monthlyPrice, setMonthlyPrice] = useState('');
    const [tokensIncluded, setTokensIncluded] = useState('0');
    const [maxActiveCases, setMaxActiveCases] = useState('-1');
    const [features, setFeatures] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        await dispatch(fetchPlansFromAPI());
        setLoading(false);
    };

    const resetForm = () => {
        setName('');
        setMonthlyPrice('');
        setTokensIncluded('0');
        setMaxActiveCases('-1');
        setFeatures('');
    };

    const handleAdd = () => {
        setSelectedPlan(null);
        resetForm();
        setIsModalOpen(true);
    };

    const handleEdit = (plan) => {
        setSelectedPlan(plan);
        setName(plan.name || '');
        setMonthlyPrice(plan.monthlyPrice != null ? String(plan.monthlyPrice) : '');
        setTokensIncluded(plan.tokensIncluded != null ? String(plan.tokensIncluded) : '0');
        setMaxActiveCases(plan.maxActiveCases != null ? String(plan.maxActiveCases) : '-1');
        setFeatures(plan.features || '');
        setIsModalOpen(true);
    };

    const handleDelete = (plan) => {
        setPlanToDelete(plan);
        setIsDeleteDialogOpen(true);
    };

    const handleSave = async () => {
        const dto = {
            name,
            monthlyPrice: parseFloat(monthlyPrice) || 0,
            tokensIncluded: parseInt(tokensIncluded) || 0,
            maxActiveCases: parseInt(maxActiveCases) || 0,
            features: features || null,
        };
        if (selectedPlan) {
            await dispatch(updatePlanInAPI(selectedPlan.id, dto));
        } else {
            await dispatch(createPlanInAPI(dto));
        }
        setIsModalOpen(false);
        loadData();
    };

    const confirmDelete = () => {
        dispatch(deletePlanFromAPI(planToDelete.id));
        setIsDeleteDialogOpen(false);
    };

    const getStatusText = (active) => active ? 'Activo' : 'Inactivo';

    const columns = [
        { name: 'Nombre', selector: row => row.name, sortable: true, grow: 2 },
        { name: 'Precio mensual', selector: row => row.monthlyPrice, sortable: true, cell: row => formatCLP(row.monthlyPrice) },
        { name: 'Tokens incluidos', selector: row => row.tokensIncluded, sortable: true },
        { name: 'Casos activos', selector: row => row.maxActiveCases, sortable: true, cell: row => row.maxActiveCases === -1 ? 'Ilimitado' : row.maxActiveCases },
        {
            name: 'Estado', selector: row => getStatusText(row.active), sortable: true,
            cell: row => (
                <span className={`apple-badge ${row.active ? 'apple-badge-success' : 'apple-badge-error'}`}>
                    {getStatusText(row.active)}
                </span>
            ),
        },
        {
            name: 'Acciones',
            cell: row => (
                <Box>
                    <IconButton onClick={() => handleEdit(row)} size="small" className="apple-button apple-tooltip">
                        <EditIcon fontSize="small" />
                        <span className="tooltiptext">Editar</span>
                    </IconButton>
                    <IconButton onClick={() => handleDelete(row)} size="small" className="apple-button apple-tooltip">
                        <RemoveCircleOutlineIcon fontSize="small" color="error" />
                        <span className="tooltiptext">Desactivar</span>
                    </IconButton>
                </Box>
            ),
            width: '160px',
        },
    ];

    const customStyles = {
        headRow: { style: { backgroundColor: '#F2F2F7', borderBottom: 'none' } },
        headCells: { style: { fontSize: '12px', fontWeight: '600', color: '#86868B', textTransform: 'uppercase' } },
        cells: { style: { fontSize: '14px', color: '#1D1D1F' } },
    };

    const filteredItems = (Array.isArray(plans) ? plans : []).filter(
        item => item.name && item.name.toLowerCase().includes(filterText.toLowerCase())
    );

    const subHeaderComponent = useMemo(() => (
        <Box className="apple-card" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
            <div className="apple-search-container">
                <SearchIcon className="apple-search-icon" />
                <input
                    type="text"
                    placeholder="Buscar plan"
                    onChange={e => setFilterText(e.target.value)}
                    className="apple-search-input"
                />
            </div>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} className="apple-button">
                Agregar Plan
            </Button>
        </Box>
    ), [filterText]);

    return (
        <div className="apple-container">
            <Paper className="apple-card">
                <DataTable
                    columns={columns}
                    data={filteredItems}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 30]}
                    subHeader
                    subHeaderComponent={subHeaderComponent}
                    progressPending={loading}
                    persistTableHead
                    customStyles={customStyles}
                />
            </Paper>

            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <Box className="apple-modal" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 600 }}>
                    <Typography className="apple-modal-title">
                        {selectedPlan ? 'Editar Plan de Suscripción' : 'Agregar Plan de Suscripción'}
                    </Typography>
                    <div className="apple-modal-content">
                        <TextField label="Nombre *" variant="outlined" fullWidth className="apple-input" value={name} onChange={e => setName(e.target.value)} />
                        <TextField label="Precio mensual (CLP) *" type="number" variant="outlined" fullWidth className="apple-input" value={monthlyPrice} onChange={e => setMonthlyPrice(e.target.value)} />
                        <TextField label="Tokens incluidos" type="number" variant="outlined" fullWidth className="apple-input" value={tokensIncluded} onChange={e => setTokensIncluded(e.target.value)} />
                        <TextField label="Casos activos máx (-1 = ilimitado)" type="number" variant="outlined" fullWidth className="apple-input" value={maxActiveCases} onChange={e => setMaxActiveCases(e.target.value)} />
                        <TextField label="Features (JSON)" variant="outlined" fullWidth multiline minRows={2} className="apple-input" value={features} onChange={e => setFeatures(e.target.value)} />
                    </div>
                    <div className="apple-modal-footer">
                        <Button onClick={() => setIsModalOpen(false)} className="apple-button">Cancelar</Button>
                        <Button variant="contained" className="apple-button" onClick={handleSave} disabled={!name || !monthlyPrice}>
                            {selectedPlan ? 'Guardar Cambios' : 'Crear Plan'}
                        </Button>
                    </div>
                </Box>
            </Modal>

            <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar desactivación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea desactivar el plan <strong>{planToDelete?.name}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)} className="apple-button">Cancelar</Button>
                    <Button onClick={confirmDelete} className="apple-button" autoFocus>Confirmar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default SubscriptionPlansPage;
